import { Injectable, inject, signal } from '@angular/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import type { ChecklistDraftEntry } from '../fm-work-order-checklist.util';
import { draftsToRecordRequests, buildChecklistRows } from '../fm-work-order-checklist.util';
import { ServicesWorkOrderChecklistPhotoService } from '../services-work-order-checklist-photo.service';
import { ServicesWorkOrdersApiService } from '../services-work-orders-api.service';
import { AuthSessionService } from '../../../shell/auth/auth-session.service';
import { WoChecklistDraftStoreService } from './wo-checklist-draft-store.service';
import { WoConnectivityService } from './wo-connectivity.service';
import { WoOfflineCacheService } from './wo-offline-cache.service';
import { WoOfflineOutboxService } from './wo-offline-outbox.service';
import { WO_OFFLINE_TTL_MS, type WoOutboxEntry } from './wo-offline.models';
import { photoOpsFromDrafts } from './wo-offline-outbox.logic';
import { classifyWoReplayFailure, replayFailureMessage } from './wo-offline-sync.logic';

export { photoOpsFromDrafts };

@Injectable({ providedIn: 'root' })
export class WoOfflineSyncService {
  private readonly outbox = inject(WoOfflineOutboxService);
  private readonly drafts = inject(WoChecklistDraftStoreService);
  private readonly cache = inject(WoOfflineCacheService);
  private readonly photoService = inject(ServicesWorkOrderChecklistPhotoService);
  private readonly api = inject(ServicesWorkOrdersApiService);
  private readonly auth = inject(AuthSessionService);
  private readonly connectivity = inject(WoConnectivityService);

  readonly syncing = signal(false);
  readonly lastFlushError = signal<string | null>(null);

  private flushPromise: Promise<void> | null = null;

  async flushAll(): Promise<void> {
    if (this.flushPromise) {
      return this.flushPromise;
    }
    this.flushPromise = this.runFlushAll().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  private async runFlushAll(): Promise<void> {
    if (!this.connectivity.online()) {
      return;
    }
    if (!this.auth.snapshot) {
      return;
    }
    this.syncing.set(true);
    this.lastFlushError.set(null);
    try {
      const entries = await this.outbox.list();
      for (const entry of entries) {
        await this.flushEntry(entry);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      this.lastFlushError.set(message);
    } finally {
      this.syncing.set(false);
    }
  }

  private async flushEntry(entry: WoOutboxEntry): Promise<void> {
    let ops = [...entry.ops];
    let draftsMap = (await this.drafts.load(entry.workOrderId))?.drafts ?? {};

    while (ops.length > 0) {
      const op = ops[0];
      if (op.type === 'PHOTO_UPLOAD') {
        try {
          const imageUrl = await this.photoService.uploadLocalChecklistPhoto(
            entry.workOrderId,
            op.order,
            op.localPath,
            op.contentType,
          );
          const current = draftsMap[op.order] ?? { responseText: '', imageUrl: '', localPhotoPath: '' };
          draftsMap = {
            ...draftsMap,
            [op.order]: {
              ...current,
              imageUrl,
              localPhotoPath: '',
            },
          };
          await this.drafts.save(entry.workOrderId, entry.organizationId, entry.projectId, draftsMap);
          try {
            await Filesystem.deleteFile({ path: op.localPath, directory: Directory.Data });
          } catch {
            /* ignore missing local file */
          }
          ops = ops.slice(1);
          await this.outbox.replaceEntry({ ...entry, ops, lastError: null });
          entry = { ...entry, ops, lastError: null };
        } catch (err) {
          await this.handleReplayFailure(entry, err, 'Photo upload failed');
          return;
        }
        continue;
      }

      try {
        const cached = await this.cache.get(entry.organizationId, entry.projectId, entry.workOrderId);
        const rows = buildChecklistRows(cached ?? { id: entry.workOrderId, checklistSnapshot: [] });
        const items = draftsToRecordRequests(rows.length ? rows : this.syntheticRowsFromDrafts(draftsMap), draftsMap);
        await this.api.submitChecklist(entry.workOrderId, {
          items,
          ...(entry.completionComment ? { completionComment: entry.completionComment } : {}),
        });
        await this.clearWorkOrderLocal(entry);
        return;
      } catch (err) {
        await this.handleReplayFailure(entry, err, 'Checklist submit failed');
        return;
      }
    }
  }

  private syntheticRowsFromDrafts(drafts: Record<number, ChecklistDraftEntry>) {
    return Object.keys(drafts)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b)
      .map((order) => ({
        order,
        text: '',
        translationMissing: false,
        photoRequired: false,
        completedAt: null,
        responseText: null,
        imageUrl: null,
      }));
  }

  private async clearWorkOrderLocal(entry: WoOutboxEntry): Promise<void> {
    await this.outbox.remove(entry.workOrderId);
    await this.drafts.clear(entry.workOrderId);
    await this.cache.remove(entry.organizationId, entry.projectId, entry.workOrderId);
  }

  /**
   * Keep outbox + drafts unless a live GET confirms COMPLETED (idempotent replay).
   * Substring matches on COMPLETED / 409 / not in progress are not used.
   */
  private async handleReplayFailure(
    entry: WoOutboxEntry,
    err: unknown,
    fallback: string,
  ): Promise<void> {
    if (classifyWoReplayFailure({ err }) === 'auth') {
      throw err instanceof Error ? err : new Error(replayFailureMessage(err, fallback));
    }
    let confirmedStatus: string | null = null;
    try {
      const live = await this.api.getWorkOrder(entry.workOrderId);
      confirmedStatus = live.status ?? null;
    } catch {
      confirmedStatus = null;
    }
    const classified = classifyWoReplayFailure({
      err,
      confirmedStatus,
      createdAt: entry.createdAt,
      nowMs: Date.now(),
      ttlMs: WO_OFFLINE_TTL_MS,
    });
    if (classified === 'already_completed') {
      await this.clearWorkOrderLocal(entry);
      return;
    }
    const message = replayFailureMessage(err, fallback);
    if (classified === 'dead_letter') {
      await this.outbox.moveToDeadLetter(entry, message);
      this.lastFlushError.set(message);
      return;
    }
    await this.outbox.setLastError(entry.workOrderId, message);
    this.lastFlushError.set(message);
  }
}
